import { IGraphColors, IVectorMaterialInfo } from '@/version-types';
import {
    checkColor,
    convertPercentColorToRGBA,
    convertToHex,
    exportAttr,
    exportSize,
    exportStyle,
    getValue,
    hasAttrColor,
} from './file-kit';

const supperTagMap = {
    rect: 'rect',
    ellipse: 'ellipse',
    path: 'polygon',
    circle: 'polygon',
    line: 'polygon',
    polyline: 'polygon',
    polygon: 'polygon',
};

/**
 * 渲染描边值
 *
 * @param dasharray - 虚线数组
 * @param strokeWidth - 描边宽度
 */
export function parseStrokeDasharrayToRender(dasharray: string, strokeWidth: number) {
    if (!dasharray) return dasharray;
    const res = dasharray.split(',');
    return res.map((item) => parseFloat((parseFloat(item) * strokeWidth).toFixed(2))).join(',');
}

export function parseStrokeDasharrayToLayer(dasharray: string, strokeWidth: number) {
    if (!dasharray || !strokeWidth) return dasharray;
    const res = dasharray.split(',');
    return res.map((item) => parseFloat((parseFloat(item) / strokeWidth).toFixed(2))).join(',');
}

/**
 * 判断 <svg 中是否包含 fill 或者stroke
 */
function hasSvgFillOrStroke(svgText) {
    // 匹配<svg>标签及其所有属性
    const svgTagMatch = svgText.match(/<svg\s+([^>]*)>/);
    if (!svgTagMatch || !svgTagMatch[1]) return false;

    const svgAttrs = svgTagMatch[1];

    // 匹配fill属性
    const fillMatch = svgAttrs.match(/\bfill=["']([^"']*)["']/);
    if (
        fillMatch &&
        fillMatch[1] &&
        fillMatch[1].toLowerCase() !== 'transparent' &&
        fillMatch[1].toLowerCase() !== 'none'
    ) {
        return true;
    }

    // 匹配stroke属性
    const strokeMatch = svgAttrs.match(/\bstroke=["']([^"']*)["']/);
    if (
        strokeMatch &&
        strokeMatch[1] &&
        strokeMatch[1].toLowerCase() !== 'transparent' &&
        strokeMatch[1].toLowerCase() !== 'none'
    ) {
        return true;
    }

    // 匹配内联样式中的fill或stroke
    const styleMatch = svgAttrs.match(/\bstyle=["']([^"']*)["']/);
    if (styleMatch && styleMatch[1]) {
        const style = styleMatch[1];

        // 检查style中的fill
        const styleFill = style.match(/\bfill\s*:\s*([^;]*)/);
        if (
            styleFill &&
            styleFill[1] &&
            styleFill[1].toLowerCase() !== 'transparent' &&
            styleFill[1].toLowerCase() !== 'none'
        ) {
            return true;
        }

        // 检查style中的stroke
        const styleStroke = style.match(/\bstroke\s*:\s*([^;]*)/);
        if (
            styleStroke &&
            styleStroke[1] &&
            styleStroke[1].toLowerCase() !== 'transparent' &&
            styleStroke[1].toLowerCase() !== 'none'
        ) {
            return true;
        }
    }

    return false;
}

function fixTagDefaultFill(text, defaultFillColor = '#000') {
    // Parse and merge the class styles from <style>
    const classStyles = {};
    const styleTagMatch = text.match(/<style.*?>([\s\S]*?)<\/style>/i);
    if (styleTagMatch) {
        const styles = styleTagMatch[1];
        styles.split('}').forEach((style) => {
            style = style.trim();
            if (!style) return;
            const [classNamesString, rules] = style.split('{').map((part) => part.trim());
            const classNames = classNamesString.split(',').map((name) => name.replace('.', '').trim());
            classNames.forEach((className) => {
                classStyles[className] = classStyles[className] || '';
                classStyles[className] += `${rules} `;
            });
        });
        text = text.replace(styleTagMatch[0], ''); // Remove style block after parsing
    }

    /**
     * 判断 <svg 中是否包含 fill 或者stroke
     */

    // 处理class 和 style
    return text.replace(
        /<(g|rect|ellipse|path|circle|line|polyline|polygon)(\s[^>]*)?>/gi,
        (match, tagName, attributes = '') => {
            attributes = attributes || '';
            let fill = hasAttrColor(attributes, 'fill');
            const classAttr = exportAttr(attributes, 'class');
            let existingStyle = '';
            if (classAttr) {
                classAttr
                    .map((className) => className.trim())
                    .forEach((className) => {
                        const classStyle = classStyles[className];
                        if (classStyle) {
                            existingStyle += `${classStyle} `;
                            fill = true;
                        }
                    });
            }
            // 移除class 和 fill 属性
            const attrWithoutClassAndStyle = attributes.replace(/(?:class|style)=["'][^"']*["']/gi, '');
            /** 当没有fill 并且也没有描边时，添加默认填充颜色
             *  2024-04-23 补充规则，当<svg 标签上不存在file 或者stroke 时才加默认fill
             * */
            let fillAttr = '';
            const stroke = hasAttrColor(attributes, 'stroke');
            if (tagName !== 'g' && !fill && !stroke && !hasSvgFillOrStroke(text)) {
                fillAttr = ` fill="${defaultFillColor}"`;
            }
            // 提取现有的内联样式并合并提取的class样式
            const inlineStyleMatch = attributes.match(/style=["']([^"']+)["']/i);
            if (inlineStyleMatch) {
                existingStyle = `${inlineStyleMatch[1]} ${existingStyle}`.trim();
            }
            const styleAttr = existingStyle ? `style="${existingStyle}"` : '';
            if (attrWithoutClassAndStyle.endsWith('/')) {
                return `<${tagName}${attrWithoutClassAndStyle.slice(0, -1)} ${fillAttr} ${styleAttr}/>`;
            }
            return `<${tagName}${attrWithoutClassAndStyle} ${fillAttr} ${styleAttr}>`;
        },
    );
}

function updateSvgColors(svgString: string): string {
    // 正则表达式以匹配 rgb(a) 颜色格式
    const colorPattern =
        /rgba?\(\s*(\d+(\.\d+)?%?)\s*,\s*(\d+(\.\d+)?%?)\s*,\s*(\d+(\.\d+)?%?)\s*(?:,\s*([\d.]+%?))?\s*\)/gi;
    return svgString.replace(colorPattern, (match) => {
        // 使用 convertPercentColorToRGBA 函数对颜色值进行转换
        return convertPercentColorToRGBA(match);
    });
}

/**
 * 拍平复杂图形颜色
 *
 * @param colors - 颜色列表
 */
export function flatGraphColors(colors: IGraphColors[]) {
    const res: string[] = [];
    colors?.length &&
        colors.forEach((item) => {
            const { color, values } = item;
            values.forEach((val) => {
                res[val] = color;
            });
        });
    return res;
}

/**
 * 打包复杂图形颜色
 *
 * @param colors - 颜色列表
 */
export function packGraphColors(colors: string[]) {
    const res: IGraphColors[] = [];
    const map: { [key: string]: number[] } = {};
    colors?.length &&
        colors.forEach((color, index) => {
            map[color] = map[color] || [];
            map[color].push(index);
        });
    Object.keys(map).forEach((color) => {
        res.push({
            color,
            values: map[color],
        });
    });
    return res;
}

export function findSingleSvgChild(text: string) {
    // // Step 1: 使用正则表达式匹配文本中的 <svg> 标签及其内容
    // const svgTagRegex = /<svg[^>]*>([\s\S]*?)<\/svg>/i;
    // // Step 2: 查找是否有 <svg> 标签，并捕获其中的内容
    // const svgContentMatch = text.match(svgTagRegex);
    // if (!svgContentMatch || !svgContentMatch[1]) {
    //     return null;1
    // }
    // // Step 3: 获取 <svg> 标签内的内容，并进行修剪（去除前后的空白字符）
    // const svgContent = svgContentMatch[1].trim();
    // // Step 4: 使用正则表达式匹配所有支持的 SVG 元素标签
    // const elements: any[] = [];
    // const elementRegex = /<([a-zA-Z]+)([^<>]*)(\/>|>([\s\S]*?)<\/\1>)/g;
    // let elementMatch;
    // while ((elementMatch = elementRegex.exec(svgContent)) !== null) {
    //     const tagName = elementMatch[1].toLowerCase();
    //     if (tagName in supperTagMap) {
    //         elements.push(tagName);
    //     }
    // }
    // // Step 5: 检查找到的元素数量并返回相应结果
    // if (elements.length === 1) {
    //     return elements[0];
    // }
    // return null;
    const svgTagRegex = /<svg[^>]*>([\s\S]*?)<\/svg>/i;
    const svgContentMatch = text.match(svgTagRegex);
    if (!svgContentMatch || !svgContentMatch[1]) {
        return null;
    }
    const svgContent = svgContentMatch[1].trim();

    const elementRegex = /<([a-zA-Z]+)([^<>]*)(\/>|>([\s\S]*?)<\/\1>)/;
    const elementMatch = svgContent.match(elementRegex);
    if (elementMatch && elementMatch[1]) {
        const remainingContent = svgContent.substring(elementMatch.index || 0 + elementMatch[0].length).trim();
        if (!remainingContent.match(elementRegex)) {
            return elementMatch[1];
        }
    }
    return null;
}
/**
 * 解析简单 SVG 内容
 *
 * @param text - 文本
 */
function parseShape(text: string, width: number, height: number) {
    // 过滤<path> <rect>上的宽高
    const elementName = findSingleSvgChild(text);
    if (!elementName) return null;
    // 只有一个路径时，解析成简单图形
    let vectorType = elementName.toLocaleLowerCase();
    if (!supperTagMap[vectorType]) {
        return null;
    }
    const fill = getValue(text, 'fill');
    const stroke = getValue(text, 'stroke');
    let strokeLinecap = getValue(text, 'stroke-linecap') || '';
    strokeLinecap = strokeLinecap === 'butt' ? '' : strokeLinecap;
    const fillRule = getValue(text, 'fill-rule') || 'nonzero';
    const strokeWidth = Math.min(Math.max(parseFloat(getValue(text, 'stroke-width')), 0), 100) || 0;
    const strokeDasharray = parseStrokeDasharrayToLayer(getValue(text, 'stroke-dasharray') || '', strokeWidth);
    let radius = 0;
    if (vectorType === 'rect') {
        radius = parseFloat(exportAttr(text, 'rx')[0] || exportAttr(text, 'ry')[0]) || 0;
        radius = Math.max(Math.round(radius), 0);
        const size = exportSize(text);
        width = size.width;
        height = size.height;
    } else if (vectorType === 'ellipse') {
        const rx = Math.ceil(parseFloat(exportAttr(text, 'rx')[0]) || 0);
        if (rx) {
            width = rx * 2;
        }
        const ry = Math.ceil(parseFloat(exportAttr(text, 'ry')[0]) || 0);
        if (ry) {
            height = ry * 2;
        }
    } else if (vectorType === 'circle') {
        const r = Math.ceil(parseFloat(exportAttr(text, 'r')[0]) || 0);
        if (r) {
            width = height = r * 2;
        }
        vectorType = 'ellipse';
    }

    text = text.replace(
        /(<(?:path|polygon|rect|ellipse|circle|line|polyline)[^>]*?)(\/>|>)/gi,
        (match, tagStart, tagEnd) => {
            // 先移除已存在的 data-need-clippath 属性
            const cleanedTagStart = tagStart.replace(/\s+data-need-clippath="[^"]*"/gi, '');
            return `${cleanedTagStart} data-need-clippath="false"${tagEnd}`;
        },
    );

    return {
        info: {
            naturalWidth: width,
            naturalHeight: height,
            vectorType: supperTagMap[vectorType],
            fill: checkColor(fill) ? convertToHex(fill) : '#ffffff00',
            fillGradient: null,
            stroke: checkColor(stroke) ? convertToHex(stroke) : '#ffffff00',
            strokeGradient: null,
            strokeWidth,
            strokeDasharray,
            strokeLinecap,
            radius,
            colors: [],
            blur: 0,
            strokeHidden: false,
            fillRule,
            effect: {
                shadow: [],
                reflection: [],
            },
        } as IVectorMaterialInfo,
        text,
    };
}

/**
 * 解析 SVG 内容
 *
 * @param text - 文本
 */
export function parseVector(text: string) {
    // 去掉多余空格，注释
    text = text
        .trim()
        .replace(/>\s+</g, '><')
        .replace(/<!--[\s\S]*?-->/g, '');
    const { width, height } = exportSize(text);
    1;
    text = text
        .replace(/(<svg[^>]*?width=")\d+[^"]*(")/, `$1${width}$2`)
        .replace(/(<svg[^>]*?height=")\d+[^"]*(")/, `$1${height}$2`);
    // 转换currentColor
    text = text.replace(/currentColor/gi, '#000000ff');

    // 针对没有viewBox的svg，添加viewBox
    if (!/viewBox/.test(text)) {
        text = text.replace('<svg', `<svg viewBox="0 0 ${width} ${height}" `);
    }
    text = updateSvgColors(text);

    // 判断 SVG 字符串中是否存在 preserveAspectRatio
    if (!/preserveAspectRatio/.test(text)) {
        // 不存在，使用字符串替换的方法添加该属性
        text = text.replace('<svg', '<svg preserveAspectRatio="none"');
    } else {
        // 存在，使用字符串替换的方法将其值设置为 none
        text = text.replace(/preserveAspectRatio="[^"]*"/, 'preserveAspectRatio="none"');
    }

    // 正则表达式匹配 <clipPath> 到 </clipPath>
    const clipPathRegex: RegExp = /<clipPath[\s\S]*?<\/clipPath>/g;
    // 存放匹配到的 <clipPath> 标签内容的数组，并注明数组里的元素类型为字符串
    const clipPaths: string[] = [];
    // 替换 <clipPath>...</clipPath> 为 <replaceClipPath>
    text = text.replace(clipPathRegex, (match: string): string => {
        // 将匹配内容添加到数组中
        clipPaths.push(match);
        // 返回替换标签
        return '<replaceClipPath>';
    });
    //text 先把clip-path标签缓存起来，等处理完标签样式时再加回来
    text = fixTagDefaultFill(text);
    clipPaths?.length &&
        clipPaths.forEach((clipPathContent: string, index: number) => {
            // 在 text 中找到 <replaceClipPath> 并替换为对应的 clipPathContent
            text = text.replace('<replaceClipPath>', clipPathContent);
        });
    const info = parseShape(text, width, height);
    if (info) return info;

    const colors: string[] = [
        // 填充 fill
        ...exportAttr(text, 'fill'),
        ...exportStyle(text, 'fill'),
        // 描边 stroke
        ...exportAttr(text, 'stroke'),
        ...exportStyle(text, 'stroke'),
        // 渐变 stop-color
        ...exportAttr(text, 'color'),
        ...exportStyle(text, 'color'),
    ];
    // console.log('fill', exportAttr(text, 'fill'), exportStyle(text, 'fill'))

    const finalColors: { color: string; hex: string }[] = [];
    for (let i = 0; i < colors.length; i++) {
        finalColors.push({
            color: colors[i],
            hex: convertToHex(colors[i]),
        });
    }
    finalColors.sort((a, b) => {
        return a.hex.localeCompare(b.hex);
    });

    const resColors: string[] = [];
    const colorsLen4: { color: string; index: number }[] = [];
    const colorsLen7: { color: string; index: number }[] = [];
    finalColors.forEach((item, index) => {
        resColors.push(item.hex);
        if (item.color.length === 4) {
            colorsLen4.push({ color: item.color, index });
        } else if (item.color.length === 7) {
            colorsLen7.push({ color: item.color, index });
        } else {
            text = text.replace(item.color, `{{colors[${index}]}}`);
        }
    });
    colorsLen7?.length &&
        colorsLen7.forEach((item) => {
            text = text.replace(item.color, `{{colors[${item.index}]}}`);
        });
    colorsLen4?.length &&
        colorsLen4.forEach((item) => {
            text = text.replace(item.color, `{{colors[${item.index}]}}`);
        });

    return {
        info: {
            naturalWidth: width,
            naturalHeight: height,
            vectorType: 'graph',
            fill: '',
            fillGradient: null,
            stroke: '',
            strokeGradient: null,
            strokeWidth: 0,
            strokeDasharray: '',
            strokeLinecap: '',
            radius: 0,
            colors: packGraphColors(resColors),
            blur: 0,
            strokeHidden: false,
            effect: {
                shadow: [],
                reflection: [],
            },
        } as IVectorMaterialInfo,
        text,
    };
}
